import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import {
  destroySession,
  getAgentById,
  initApiToken,
  initPosSession,
} from "../actions/sessionActions";
import { IGetAgentDetailsResponse } from "../services/AgencyService";
import { SessionApi } from "../services/SessionService";

export interface ISessionState {
  token: string;
  source: string;
  name: string;
  agent: string;
  agency: string;
  sessionKey: string;
  isLoading: boolean;
  isInitialized: boolean;
  agentDetails: IGetAgentDetailsResponse | null;
}

const initialState: ISessionState = {
  token: "",
  source: "",
  name: "",
  agent: "",
  agency: "",
  sessionKey: "",
  isLoading: false,
  isInitialized: false,
  agentDetails: null,
};

const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    updateToken(state: ISessionState, action: PayloadAction<string>) {
      state.token = action.payload;
      state.isInitialized = true;
    },
    updateSource(state: ISessionState, action: PayloadAction<string>) {
      state.source = action.payload;
    },
    updateAgentName(state: ISessionState, action: PayloadAction<string>) {
      state.name = action.payload;
    },
    updateSessionKey(state: ISessionState, action: PayloadAction<string>) {
      state.sessionKey = action.payload;
    },
    updateAgency(state: ISessionState, action: PayloadAction<string>) {
      state.agency = action.payload;
    },
    updateAgent(state: ISessionState, action: PayloadAction<string>) {
      state.agent = action.payload;
    },
    setAgentDetails: (
      state,
      action: PayloadAction<IGetAgentDetailsResponse>,
    ) => {
      state.agentDetails = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initApiToken.pending, (state) => {
        state.isInitialized = false;
      })
      .addCase(initPosSession.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(initApiToken.fulfilled, (state, action) => {
        state.token = action.payload?.token ?? "";
        state.isInitialized = true;
      })
      .addCase(initPosSession.fulfilled, (state, action) => {
        state.sessionKey = action.payload?.key ?? "";
        state.isLoading = false;
        state.isInitialized = true;
      })
      .addCase(destroySession.fulfilled, (state) => {
        state.name = "";
        state.agent = "";
        state.agency = "";
        state.sessionKey = "";
        state.agentDetails = null;
      })
      .addCase(initApiToken.rejected, (state) => {
        state.isInitialized = true;
      })
      .addCase(initPosSession.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getAgentById.fulfilled, (state, action) => {
        if (action.payload) {
          state.agentDetails = action.payload;
        }
      })
      .addMatcher(
        SessionApi.endpoints.initAgentSession.matchPending,
        (state) => {
          state.isLoading = true;
        },
      )
      .addMatcher(
        SessionApi.endpoints.initAgentSession.matchFulfilled,
        (state, action) => {
          state.name = action.payload?.name ?? "";
          state.agent = action.payload?.id ?? "";
          state.agency = action.payload?.agency ?? "";
          state.isLoading = false;
        },
      )
      .addMatcher(
        SessionApi.endpoints.initAgentSession.matchRejected,
        (state) => {
          state.isLoading = false;
        },
      );
  },
});

export const {
  updateToken,
  updateSource,
  updateAgentName,
  updateSessionKey,
  updateAgency,
  updateAgent,
} = sessionSlice.actions;

export default sessionSlice.reducer;
